import {Component, computed, inject, input, signal, TemplateRef, output} from "@angular/core";
import {AssessableContextService} from "../../../services/assessment-context.service";
import {AssessableItem, AssessableItemGroup} from "@models";
import {Translations, TranslationToken} from "@translations";
import {AssessableUiService} from "../../../services/assessable-ui.service";
import {AssessablePanelMode} from "@common/components/assessment/utils";
import {AssessableAnswerService} from "../../../services/assessment-answer.service";
import {GeneratedTypePredicates, TrackByMethods, TypePredicates} from "@services";
import {zoomInOnEnterAnimation, zoomOutOnLeaveAnimation} from "angular-animations";
import {AssessableProxyService} from "../../../services/assessable-proxy.service";
import {startWith} from "rxjs/operators";
import {UntilDestroy, untilDestroyed} from "@ngneat/until-destroy";

@UntilDestroy()
@Component({
	selector: "sim-assessable-group",
	templateUrl: "./assessable-group.component.html",
	styleUrl: "./assessable-group.component.scss",
	animations: [zoomInOnEnterAnimation({duration: 250}), zoomOutOnLeaveAnimation({duration: 250})],
	standalone: false,
})
export class AssessableGroupComponent<T extends AssessableItem> {
	// Enums
	protected readonly T = Translations;
	protected readonly AssessablePanelMode = AssessablePanelMode;
	protected readonly GeneratedTypePredicates = GeneratedTypePredicates;
	protected readonly TrackByMethods = TrackByMethods;

	// Inputs
	group = input.required<AssessableItemGroup<T>>();
	groupIndex = input.required<number>();
	scoreTemplate = input<TemplateRef<any>>();
	previewElement = input<HTMLElement>();
	itemTemplateRef = input<TemplateRef<any>>();
	addItemToGroupLocalizationKey = input<TranslationToken>();

	// Outputs
	onClickChangeActivationCondition = output<AssessableItemGroup<T>>();

	// injections
	protected store = inject(AssessableUiService);
	protected contextService = inject(AssessableContextService);
	protected proxyService = inject(AssessableProxyService);
	private _answerService = inject(AssessableAnswerService);

	// signals
	public isGroupInEditMode = computed(() => this.store.groupInEditMode() === this.group().groupId);
	public isGroupExpanded = computed(() => this.store.groupExpanded() === this.group().groupId);

	public groupHtmlId = computed(() => "assessableGroup_" + this.group().groupId);
	public isSaveButtonDisabled = computed(() => !this._isGroupValid());
	public isEditButtonDisabled = computed(() => this._computeIsEditButtonDisabled());
	public hasKoItem = computed(() => this._computeHasKoItem());

	private _isGroupValid = signal(false);

	public ngOnInit() {
		const groupControl = this.contextService.form.controls.assessableGroups.at(this.groupIndex());
		groupControl.statusChanges.pipe(untilDestroyed(this), startWith(groupControl.status)).subscribe((status) => {
			this._isGroupValid.set(status === "VALID");
		});
	}

	// this is no signal on purpose because of the complications of contextService and answerService
	public get isGroupVisible(): boolean {
		if (this.store.isEditPanel()) {
			return true;
		}

		if (this.group().items.length === 0) {
			return false;
		}

		if (this.store.mode() !== AssessablePanelMode.Answer) {
			return true;
		}

		return this._answerService.isGroupActivated(this.group().groupId);
	}

	protected groupItems = computed(() => this.group().items.length);
	protected groupItemsAnswered = computed(() => {
		const groupMap = this._answerService.answeredItemsMap().get(this.group().groupId);
		if (!groupMap) {
			return 0;
		}

		return Array.from(groupMap.values()).filter(Boolean).length;
	});
	protected isGroupAnswered = computed(() => this.groupItems() === this.groupItemsAnswered());

	public handleAfterExpand() {
		const htmlElement = window.document.getElementById(this.groupHtmlId());
		htmlElement?.scrollIntoView();
	}

	public togglePanel() {
		if (this.isGroupExpanded()) {
			this.store.handleGroupCollapse();
		} else {
			this.store.handleGroupExpand(this.group().groupId);
		}
	}

	public expandPreviousGroup() {
		this._expandGroupRelative(-1);
	}

	public expandNextGroup() {
		this._expandGroupRelative(1);
	}

	private _expandGroupRelative(relativeIndex: number) {
		const assessable = this.contextService.getAssessable();
		const groupIndex = assessable.assessableGroups.findIndex((group) => group.groupId === this.group().groupId);
		const groupToExpand = assessable.assessableGroups[groupIndex + relativeIndex];
		this.store.handleGroupExpand(groupToExpand.groupId);
	}

	private _computeIsEditButtonDisabled() {
		// never disabled for editing while nothing is in edit mode
		if (this.store.groupInEditMode() === null && this.store.itemInEditMode() === null) {
			return false;
		}

		return !this.contextService.isFormValid();
	}

	private _computeHasKoItem() {
		const isEditPanel = this.store.isEditPanel();
		const group = this.group();

		if (isEditPanel) {
			return group.items.some((item) => {
				if (TypePredicates.isAssessmentItem(item)) {
					return item.isKnockoutQuestion;
				}
				return false;
			});
		}
		return false;
	}
}
